import { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Zap, X, Loader2, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  bulkBannerTotal,
  useBulkProcessBannerStore,
} from '@/stores/bulkProcessBannerStore';
import {
  bannerDeferredCount,
  displayReadyCount,
  usePendingBillingStore,
} from '@/stores/pendingBillingStore';
import ProcessPendingConfirmDialog from '@/components/billing/ProcessPendingConfirmDialog';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSyncPendingBillingCount } from '@/hooks/useSyncPendingBillingCount';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { onPlanChanged } from '@/lib/billingEvents';
import { canProcessPendingApplications } from '@/lib/pendingProcessing';

const DISMISSED_KEY_PREFIX = 'hiromatch_pending_banner_dismissed';

interface DismissState {
  at: string;
  count: number;
  companyId: string;
}

function dismissStorageKey(companyId: string): string {
  return `${DISMISSED_KEY_PREFIX}:${companyId}`;
}

function readDismiss(companyId: string | undefined): DismissState | null {
  if (!companyId) return null;
  try {
    const scopedRaw = sessionStorage.getItem(dismissStorageKey(companyId));
    if (!scopedRaw) return null;
    const parsed = JSON.parse(scopedRaw) as DismissState;
    if (parsed.companyId && parsed.companyId !== companyId) return null;
    return { ...parsed, companyId };
  } catch {
    return null;
  }
}

/** One-time: old builds stored dismiss in localStorage and hid the banner across sessions. */
function clearLegacyDismissStorage(companyId: string): void {
  try {
    localStorage.removeItem(DISMISSED_KEY_PREFIX);
    localStorage.removeItem(dismissStorageKey(companyId));
  } catch {
    // ignore
  }
}

export default function PostUpgradeBanner() {
  const { selectedCompany } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const pendingUpgrade = usePendingBillingStore(s => s.pendingUpgrade);
  const readyToProcess = usePendingBillingStore(s => s.readyToProcess);
  const billingRetry = usePendingBillingStore(s => s.billingRetry);
  const bulkPhase = useBulkProcessBannerStore(s => s.phase);
  const bulkCompanyId = useBulkProcessBannerStore(s => s.companyId);
  const bulkCompleted = useBulkProcessBannerStore(s => s.completed);
  const bulkProgressKnown = useBulkProcessBannerStore(s => s.progressKnown);
  const bulkInFlight = useBulkProcessBannerStore(s => s.inFlightCount);
  const inboundScreeningCount = useBulkProcessBannerStore(s => s.inboundScreeningCount);
  const bulkTargetIds = useBulkProcessBannerStore(s => s.targetIds);
  const { data: billingStatus } = useBillingStatus();
  const hasProcessingCredits = canProcessPendingApplications(billingStatus);
  const [dismiss, setDismiss] = useState<DismissState | null>(null);

  useEffect(() => {
    if (!selectedCompany?.id) {
      setDismiss(null);
      return;
    }
    clearLegacyDismissStorage(selectedCompany.id);
    setDismiss(readDismiss(selectedCompany.id));
  }, [selectedCompany?.id]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useSyncPendingBillingCount();

  const deferred = { pendingUpgrade, readyToProcess, billingRetry };
  const count = bannerDeferredCount(deferred);
  const isUpgradeMode = !hasProcessingCredits && count > 0;
  const toProcess = displayReadyCount(deferred, hasProcessingCredits);
  const bulkTotal = bulkBannerTotal({ targetIds: bulkTargetIds });
  const showBulkProgress =
    bulkPhase === 'active'
    && bulkCompanyId === selectedCompany?.id
    && bulkTotal > 0
    && bulkProgressKnown
    && (bulkCompleted > 0 || bulkInFlight > 0);

  const remainingCredits = useMemo(() => {
    if (!billingStatus || billingStatus.isUnlimited) return null;
    const b = billingStatus.balances?.candidates;
    if (!b || b.unlimited) return null;
    return Math.max(0, b.remaining);
  }, [billingStatus]);

  useEffect(() => {
    if (!selectedCompany?.id) return;
    const key = dismissStorageKey(selectedCompany.id);
    const onStorage = (e: StorageEvent) => {
      if (e.key === key) setDismiss(readDismiss(selectedCompany.id));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [selectedCompany?.id]);

  // Re-show when BE reports more processable rows than when the user dismissed.
  useEffect(() => {
    if (!selectedCompany?.id || !dismiss) return;
    if (dismiss.companyId !== selectedCompany.id) {
      setDismiss(null);
      return;
    }
    if (toProcess > dismiss.count) {
      try {
        sessionStorage.removeItem(dismissStorageKey(selectedCompany.id));
      } catch {
        // ignore
      }
      setDismiss(null);
    }
  }, [selectedCompany?.id, toProcess, dismiss]);

  const wasBulkProgressRef = useRef(false);
  useEffect(() => {
    if (wasBulkProgressRef.current && !showBulkProgress && toProcess > 0) {
      setDismiss(null);
    }
    wasBulkProgressRef.current = showBulkProgress;
  }, [showBulkProgress, toProcess]);

  useEffect(() => {
    return onPlanChanged(() => {
      try {
        if (selectedCompany?.id) {
          sessionStorage.removeItem(dismissStorageKey(selectedCompany.id));
        }
      } catch {
        // ignore
      }
      setDismiss(null);
    });
  }, []);

  if (!selectedCompany) return null;
  if (!showBulkProgress && count <= 0) return null;
  if (!showBulkProgress && dismiss && dismiss.count >= count) return null;

  const handleDismiss = () => {
    if (!selectedCompany) return;
    const next: DismissState = {
      at: new Date().toISOString(),
      count: toProcess > 0 ? toProcess : count,
      companyId: selectedCompany.id,
    };
    try {
      sessionStorage.setItem(dismissStorageKey(selectedCompany.id), JSON.stringify(next));
    } catch {
      // ignore
    }
    setDismiss(next);
  };

  const openApplicationsQueue = () => {
    setConfirmOpen(false);
    const status = hasProcessingCredits ? 'READY_TO_PROCESS' : 'PENDING_UPGRADE';
    navigate(`/applications?status=${status}&selectReady=1`, {
      state: { selectProcessable: true },
    });
  };

  const containerClass = isUpgradeMode
    ? 'border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/30'
    : 'border-emerald-300 bg-emerald-50 dark:border-emerald-700/60 dark:bg-emerald-950/30';
  const titleClass = isUpgradeMode
    ? 'text-amber-900 dark:text-amber-100'
    : 'text-emerald-900 dark:text-emerald-100';
  const bodyClass = isUpgradeMode
    ? 'text-amber-800/90 dark:text-amber-200/80'
    : 'text-emerald-800/90 dark:text-emerald-200/80';
  const dismissClass = isUpgradeMode
    ? 'text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100'
    : 'text-emerald-700 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-100';
  const Icon = isUpgradeMode ? Lock : Zap;
  const iconClass = isUpgradeMode
    ? 'text-amber-700 dark:text-amber-300'
    : 'text-emerald-700 dark:text-emerald-300';

  const progressContainerClass =
    'border-emerald-300 bg-emerald-50 dark:border-emerald-700/60 dark:bg-emerald-950/30';
  const progressTitleClass = 'text-emerald-900 dark:text-emerald-100';
  const progressBodyClass = 'text-emerald-800/90 dark:text-emerald-200/80';

  const title = showBulkProgress
    ? t('application.pendingBilling.bannerProgressTitle', { done: bulkCompleted, total: bulkTotal })
    : isUpgradeMode
      ? t('application.pendingUpgrade.bannerTitle', { count })
      : t('application.pendingBilling.bannerTitle', { count: toProcess > 0 ? toProcess : count });
  const body = showBulkProgress
    ? t('application.pendingBilling.bannerProgressBody', { done: bulkCompleted, total: bulkTotal })
    : isUpgradeMode
      ? t('application.pendingUpgrade.bannerBody')
      : t('application.pendingBilling.bannerBody', { count: toProcess > 0 ? toProcess : count });
  const inboundBody =
    showBulkProgress && inboundScreeningCount > 0
      ? t('application.pendingBilling.bannerInboundScreening', { count: inboundScreeningCount })
      : null;

  const bannerContainerClass = showBulkProgress ? progressContainerClass : containerClass;
  const bannerTitleClass = showBulkProgress ? progressTitleClass : titleClass;
  const bannerBodyClass = showBulkProgress ? progressBodyClass : bodyClass;
  const BannerIcon = showBulkProgress ? Loader2 : Icon;
  const bannerIconClass = showBulkProgress
    ? `mt-0.5 h-4 w-4 shrink-0 animate-spin text-emerald-700 dark:text-emerald-300`
    : `mt-0.5 h-4 w-4 shrink-0 ${iconClass}`;

  return (
    <>
      <div
        className={`relative flex flex-col gap-3 rounded-lg border p-3 text-sm sm:flex-row sm:items-start sm:gap-3 ${!showBulkProgress ? 'pr-10' : ''} ${bannerContainerClass}`}
      >
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <BannerIcon className={bannerIconClass} />
          <div className="min-w-0 flex-1">
            <p className={`font-medium ${bannerTitleClass}`}>{title}</p>
            <p className={`mt-0.5 text-xs ${bannerBodyClass}`}>{body}</p>
            {inboundBody ? (
              <p className={`mt-1 text-xs ${bannerBodyClass}`}>{inboundBody}</p>
            ) : null}
          </div>
        </div>
        {!showBulkProgress && (
          isUpgradeMode ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/profile?tab=billing')}
              className="w-full shrink-0 sm:w-auto border-amber-700 text-amber-900 hover:bg-amber-100 dark:border-amber-300 dark:text-amber-100 dark:hover:bg-amber-900/40"
            >
              {t('application.pendingUpgrade.upgradeCta')}
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={toProcess === 0}
              className="w-full shrink-0 gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700 sm:w-auto"
            >
              <Zap className="h-3.5 w-3.5" />
              {t('application.pendingBilling.reviewInApplications', { count: toProcess })}
            </Button>
          )
        )}
        {!showBulkProgress ? (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className={`absolute right-2 top-2 ${dismissClass}`}
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <ProcessPendingConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        breakdown={{
          firstTime: readyToProcess + (hasProcessingCredits ? pendingUpgrade : 0),
          billingRetry,
        }}
        variant="openApplications"
        remainingCredits={remainingCredits}
        onConfirm={openApplicationsQueue}
      />
    </>
  );
}
